import React from 'react';

export interface FormField<T> {
  value: T;
  onChange: (newValue: T, shared?: boolean) => void;
  /**
   * Use this to directly modify the state without running the function passed
   * to `sideEffect` (e.g. inter-dependent state changes or tracking).
   */
  onChangeWithoutSideEffect: (newValue: T) => void;
  id: string;
  labelId: string;
  label: string;
}

export function useFormField<T>(
  id: string,
  initialValue: T,
  label: string,
  sideEffect?: (newValue: T, shared?: boolean) => void
): FormField<T> {
  const [value, setFormValue] = React.useState<T>(initialValue);
  const onChange = React.useCallback(
    (newValue: T, shared?: boolean) => {
      setFormValue(newValue);
      sideEffect?.(newValue, shared);
    },
    [setFormValue, sideEffect]
  );

  const onChangeWithoutSideEffect = React.useCallback(
    (newValue: T) => {
      setFormValue(newValue);
    },
    [setFormValue]
  );

  const labelId = `${id}-label`;

  return React.useMemo(
    () => ({value, onChange, onChangeWithoutSideEffect, id, labelId, label}),
    [value, onChange, onChangeWithoutSideEffect, id, labelId, label]
  );
}
